<!-- This needs to be removed and all of the reference that leads to this component needs to be changed-->
<!-- See the checklist before upgrading from 0.5.4 https://bwise.atlassian.net/wiki/spaces/RC/pages/3620798543/Wizer+upgrade+checklists -->
<template>
  <div
    data-w-component="form-field"
    :data-w-id="computedName"
    v-if="shouldRender"
    v-show="!computedHidden"
    :class="{
      'w-form-field': true,
      'w-form-field--inline': computedInline,
      'w-form-field--inline-start': computedInline === 'start',
      'w-form-field--dense': computedDense,
      'w-form-field--no-padding': bare,
      [`md:w-${this.span}-12`]: this.span && this.span < 12,
    }"
  >
    <div v-if="computedShowHeader && !bare" data-w-element="header" class="w-form-field__header">
      <div v-if="inputFieldOptions.showLabel" class="w-form-field__label">
        <span class="w-form-field__label-content">{{ computedLabelContent }}</span>
      </div>
      <Icon
        v-if="computedHelpText"
        src="help"
        v-tooltip.left-start="{
          content: computedHelpText,
          loadingClass: 'hidden',
        }"
        class="ml-1"
      />
    </div>
    <div class="flex-grow overflow-auto">
      <div
        data-w-element="content"
        class="w-form-field__input"
        :class="{ 'w-form-field__input-aligned': !inputElement }"
      >
        <slot>
          <component :is="inputComponent" v-bind="computedAttrs" v-on="$listeners">
            <template v-for="(_, name) in $scopedSlots" v-slot:[name]="props">
              <slot :name="name" v-bind="props" />
            </template>
          </component>
        </slot>
      </div>
      <div v-if="!bare" data-w-element="footer" class="w-form-field__footer">
        <transition>
          <span key="error" v-if="inputError" class="w-form-field__error" data-w-element="error">
            {{ inputError }}
          </span>
          <span
            key="hint"
            v-else-if="persistHint || (computedHint && inputIsFocused)"
            class="w-form-field__hint"
          >
            {{ computedHint }}
          </span>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import Icon from '@wizer/core/components/icon/Icon'

const defaultInputFieldOptions = {
  showLabel: true,
}

const inputComponents = {
  TextField: () => import('@wizer/core/components/text-field/TextField'),
  TextArea: () => import('@wizer/core/components/text-area/TextArea'),
  Toggle: () => import('@wizer/core/components/toggle/Toggle'),
  AmountField: () => import('@wizer/core/components/amount-field/AmountField'),
  Select: () => import('@wizer/core/components/select/Select'),
  FileList: () => import('@wizer/core/components/file-list/FileList'),
  DateField: () => import('@wizer/core/components/date-field/DateField'),
  FormTable: () => import('@wizer/core/components/form/FormTable'),
  // non-default
  ColorBar: () => import('@wizer/core/components/color-bar/ColorBar'),
  RadioGroup: () => import('@wizer/core/components/radio-group/RadioGroup'),
  UrlField: () => import('@wizer/core/components/url-field/UrlField'),
}

const InputComponentMap = {
  string: inputComponents.TextField,
  plain_text: inputComponents.TextArea,
  integer: inputComponents.TextField,
  float: inputComponents.TextField,
  url: inputComponents.UrlField,
  text: inputComponents.TextArea,
  boolean: inputComponents.Toggle,
  amount: inputComponents.AmountField,
  choice: inputComponents.Select,
  file: inputComponents.FileList,
  date: inputComponents.DateField,
  table: inputComponents.FormTable,
}

export default {
  components: {
    Icon,
  },
  inheritAttrs: false,
  inject: {
    formContext: { default: null },
    formSectionContext: { default: null },
  },
  props: {
    type: {
      type: String,
      default: undefined,
    },
    name: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: undefined,
    },
    hidden: {
      type: Boolean,
      default: undefined,
    },
    hint: {
      type: String,
      default: undefined,
    },
    helpText: {
      type: [String, Function],
      default: undefined,
    },
    // layout only
    span: {
      type: [Number, String],
      default: 12,
    },
    inline: {
      type: [Boolean, String],
      default: undefined,
    },
    dense: {
      type: Boolean,
      default: undefined,
    },
    persistHint: {
      type: Boolean,
      default: false,
    },
    showHeader: {
      type: Boolean,
      default: undefined,
    },
    /**
     * @deprecated Use nested component in the slot instead
     */
    display: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      inputElement: null,
      bare: false,
    }
  },
  computed: {
    prefixAsterisk() {
      return this.computedAsteriskPlacement === 'left'
    },
    computedAsteriskPlacement() {
      return this.$wizer.formConfig?.asteriskPlacement || 'left'
    },
    computedName() {
      if (this.formContext && this.formContext.fieldNamePrefix) {
        return `${this.formContext.fieldNamePrefix}${this.name}`
      }
      return this.name
    },
    metaData() {
      if (this.formContext && this.name) {
        return this.formContext.getFieldMetaData(this.computedName)
      }
      return null
    },
    computedAttrs() {
      // converts attrs into input element props
      return _.omit(
        _.mapValues(this.$attrs, (x) => (x === '' ? true : x)),
        ['defaultValue', 'value', 'error'],
      )
    },
    // meta props
    computedType() {
      return (this.metaData && this.metaData.type) || this.type
    },
    computedLabelContent() {
      const inputRequired = this.inputRequired || this.computedAttrs.required
      if (!inputRequired) {
        return this.computedLabel
      }
      if (this.prefixAsterisk) {
        return `* ${this.computedLabel}`
      }
      return `${this.computedLabel} *`
    },
    computedLabel() {
      return this.label || (this.metaData && this.metaData.label) || ''
    },
    computedHint() {
      return this.hint || (this.metaData && this.metaData.hint)
    },
    computedHidden() {
      return this.hidden || (this.metaData && this.metaData.hidden)
    },
    computedHelpText() {
      return this.helpText || (this.metaData && this.metaData.helpText)
    },
    computedShowHeader() {
      if (this.showHeader !== undefined) {
        return this.showHeader
      }
      if (this.metaData && this.metaData.showHeader !== undefined) {
        return this.metaData && this.metaData.showHeader
      }
      return true
    },
    // layout props
    computedInline() {
      if (this.inline !== undefined) {
        return this.inline
      }
      return this.metaData && this.metaData.inline
    },
    computedDense() {
      if (this.dense !== undefined) {
        return this.dense
      }
      return this.metaData && this.metaData.dense
    },
    // from input element
    inputRequired() {
      return this.inputElement && this.inputElement.inputRequired
    },
    inputValue() {
      return this.inputElement && this.inputElement.inputValue
    },
    inputError() {
      return this.inputElement && this.inputElement.inputError
    },
    inputIsFocused() {
      return this.inputElement && this.inputElement.inputIsFocused
    },
    inputFieldOptions() {
      return {
        ...defaultInputFieldOptions,
        ...((this.inputElement && this.inputElement.inputFieldOptions) || {}),
      }
    },
    // dynamic input
    inputComponent() {
      // TODO: deprecate.display ?
      const display = this.display || (this.metaData && this.metaData.display)

      const { multiple } = this.computedAttrs
      if (this.computedType === 'choice' && multiple && display === 'table') {
        return inputComponents.FormTable
      }
      if (this.computedType === 'choice' && !multiple && display === 'colorbar') {
        return inputComponents.ColorBar
      }
      if (this.computedType === 'choice' && !multiple && display === 'radio') {
        return inputComponents.RadioGroup
      }
      const componentMap = {
        ...InputComponentMap,
        ...(this.$wizer && this.$wizer.formFieldAddOn),
      }
      // do not deprecate
      return componentMap[this.computedType]
    },
    shouldRender() {
      if (!this.computedName) {
        return true
      }
      if (this.formContext && this.formContext.hasMetaData() && !this.metaData) {
        return false
      }
      return true
    },
  },
  created() {
    if (this.formContext && this.name) {
      this.formContext.registerElement({
        type: 'field',
        id: this.computedName,
        parentId: this.formContext.parentElementId,
        name: this.computedName,
        validate: this.validate,
      })
    }
  },
  methods: {
    registerInput(element) {
      this.inputElement = element
      if (this.formContext && this.name) {
        // field bindings
        element.$on('update:value', (value) => {
          if (!this.computedHidden) {
            this.formContext.setValue(this.computedName, value)

            let valueObject

            if (this.inputElement.inputFetchedItems) {
              if (!_.isArray(value)) {
                valueObject = _.find(this.inputElement.inputFetchedItems, { id: value })
                this.formContext.setItem(
                  this.computedName,
                  (valueObject && valueObject.data) || value,
                )
              } else {
                valueObject = _.map(_.castArray(value), (id) =>
                  _.find(this.inputElement.inputFetchedItems, { id }),
                )
                this.formContext.setItem(this.computedName, valueObject)
              }
            } else {
              this.formContext.setItem(this.computedName, value)
            }
          }
        })
        element.$on('update:error', (value) => {
          this.formContext.setError(this.computedName, value)
        })
        // form bindings
        // element.$on('update:values', value => {
        //   if (!this.computedHidden) {
        //     this.formContext.setValue(this.computedName, value)
        //   }
        // })
        element.$on('update:errors', (value) => {
          if (_.some(_.toArray(value))) {
            this.formContext.setError(this.computedName, value)
          } else {
            this.formContext.setError(this.computedName, null)
          }
        })
      }
    },
    unregisterInput() {
      this.inputElement = null
    },
    getMetaData() {
      return {
        ...this.metaData,
        label: this.computedLabel,
        name: this.computedName,
        hidden: this.computedHidden,
        hasForm: !!this.formContext,
        required: (this.metaData && this.metaData.required) || this.computedAttrs.required,
      }
    },
    // public
    validate(draft) {
      if (this.inputElement) {
        this.inputElement.validate(draft)
      }
    },
  },
  provide() {
    const formSectionContext = {
      ...this.formSectionContext,
      bare: () => {
        this.bare = true
      },
    }

    return {
      formFieldContext: {
        registerInput: this.registerInput,
        unregisterInput: this.unregisterInput,
        getMetaData: this.getMetaData,
        getSource: this.formContext && this.formContext.getSource,
      },
      formSectionContext,
    }
  },
  beforeDestroy() {
    if (this.formContext && this.name) {
      this.formContext.unregisterElement({
        type: 'field',
        id: this.computedName,
      })
    }
  },
}
</script>

<style lang="postcss">
.w-form-field {
  @apply flex flex-col px-4 text-sm w-full;
}
.w-form-field__header {
  @apply text-sm text-gray-600;
  @apply flex h-6 items-start;
}
.w-form-field__label {
  @apply flex flex-grow overflow-hidden;
}
/* Only truncate inline labels as top labels width is not constraint in pixels */
.w-form-field--inline .w-form-field__label-content {
  @apply truncate;
}
.w-form-field__input-aligned {
  @apply flex -mt-px;
  padding-top: 0.55rem;
}

@screen md {
  .w-form-field.w-form-field--inline {
    @apply flex-row;
  }
  .w-form-field--inline .w-form-field__header {
    @apply w-48 flex-shrink-0 pr-3 items-center;
    margin-top: 0.4rem;
  }
  .w-form-field--inline.w-form-field--dense .w-form-field__header {
    margin-top: 0.4rem;
  }
  .w-form-field--inline .w-form-field__label {
    @apply justify-end text-right;
  }
  .w-form-field--inline.w-form-field--inline-start .w-form-field__label {
    @apply justify-start text-left;
  }
}
.w-form-field--no-padding {
  @apply p-0;

  & .w-form-table {
    @apply border-0;
  }
}
.w-form-field__footer {
  @apply max-h-full relative overflow-hidden flex items-center;
  min-height: 1.5rem;
}
.w-form-field__footer > .v-enter,
.w-form-field__footer > .v-leave-to {
  transition: transform 0.12s;
  transform: translateY(-100%);
}
.w-form-field__footer > .v-enter-to,
.w-form-field__footer > .v-leave {
  transition: transform 0.12s;
}
.w-form-field__hint {
  @apply absolute text-gray-700 opacity-50 text-xs;
  @apply w-full truncate;
}
.w-form-field__error {
  @apply w-full;
  @apply text-red-500 text-xs tracking-wide;
}

@media screen and (-webkit-min-device-pixel-ratio: 0) {
  @screen md {
    .w-form-field--inline .w-form-field__label-content {
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      @apply whitespace-normal;
      /* Needed for Mozilla Firefox to work correctly */
      text-overflow: ellipsis;
      -webkit-box-orient: vertical;
    }
  }
}
</style>
